"use client"

import {
  InfiniteTable,
  TableSkeleton,
} from "@/app/components/infinite-table/infinite-table"
import { HeaderRow } from "@/app/components/table"
import type { ItemOffer } from "@anaconda/core/bid/types"
import { Trans } from "@lingui/react/macro"
import { TableRow } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useNftReatom } from "../store/hooks.reatom"
import { Cell } from "./cells"
import { COLUMNS } from "./columns"
import { useOfferItemsReatom } from "./use-offer-items.reatom"

const getItemKey = (item: ItemOffer) => item.bid.id

export const ItemOffersTableReatom = reatomComponent(() => {
  const nft = useNftReatom()
  const { items, loadNext } = useOfferItemsReatom()

  if (items.length === 0) {
    return (
      <div className="py-10 text-center text-foreground/60">
        <Trans>No offers yet</Trans>
      </div>
    )
  }

  return (
    <InfiniteTable
      getItemKey={getItemKey}
      items={items}
      loadNext={loadNext}
      components={COMPONENTS}
      initialItemCount={15}
    />
  )
}, "ItemOffersTable")

export function ItemOffersTableSkeletonReatom() {
  return <TableSkeleton HeaderRow={OffersHeaderRow} RowSkeleton={RowSkeleton} />
}

function OffersHeaderRow() {
  return (
    <HeaderRow>
      {COLUMNS.map((column, index) => (
        <column.Header key={index} />
      ))}
    </HeaderRow>
  )
}

const RowContent = reatomComponent<{ content: ItemOffer }>(({ content }) => {
  const nft = useNftReatom()

  return (
    <>
      {COLUMNS.map((column, index) => (
        <Cell key={index}>
          <column.Content offer={content} nft={nft} />
        </Cell>
      ))}
    </>
  )
}, "ItemOfferRow")

function RowSkeleton() {
  return (
    <>
      {COLUMNS.map((column, index) => (
        <Cell key={index}>
          <column.Skeleton />
        </Cell>
      ))}
    </>
  )
}

// Module scope on purpose: react-virtuoso treats a new `components` object as
// new component types and remounts every row, which the refetch interval would
// otherwise trigger on a timer.
const OffersTableRow = (props: React.ComponentProps<typeof TableRow>) => (
  <TableRow {...props} data-testid="nft-offer" />
)

const COMPONENTS = {
  HeaderRow: OffersHeaderRow,
  RowContent,
  RowSkeleton,
  TableRow: OffersTableRow,
} as const
